// angular
// 	.module("HeroPRO")
// 	.factory("WalkthroughFactory", walkthroughFactory);

import _ from 'lodash';

/* @ngInject */
export default function walkthroughFactory(
    $q, FRInformationFactory, FRClosingFactory, FRDocumentsFactory, HPStateMachineConfig, UserAuthFactory,
    HPPipelineStages, hpLoggerService, PermissionsFactory, SelectedProductsFactory
) {
    let _walkthroughPromise;
    let _activeWalkthroughTemplate = '';
    let _stateMachineData = {};
    let _appInfo = {};
    let _fundingRequest = {};
    let _userAccess = '';
    let _isFundingScheduled = false;
    let _hasProducts = false;
    let _hasRequiredDocs = false;

    return {
        getActiveWalkthroughTemplate,
        loadWalkthrough,
    };

    function loadWalkthrough() {

        _walkthroughPromise = $q((resolve) => {
            // Before setting the active walkthrough template, ensure that all async data that is needed is available
            $q.all([
                FRInformationFactory.getFundingRequestInformationPromise(),
                FRClosingFactory.getFundingRequestClosingPromise(),
                UserAuthFactory.getAuthorization(),
                FRInformationFactory.getRequiredDocGenSetType()
            ]).then((response) => {
                _stateMachineData = FRInformationFactory.getStateMachineData();
                _appInfo = FRInformationFactory.getAppInfo();
                _userAccess = response[2] != null && response[2].HasElevatedAccess == true ? 'elevatedUser' : 'nonElevatedUser';
                _isFundingScheduled = FRClosingFactory.getIsFundingScheduled();
                _fundingRequest = FRClosingFactory.getFundingRequest();

                _hasRequiredDocs = !!(response[3].DocGenSetType);

                // adding this to always check for 
                // products so that add products template shows up when needed.
                SelectedProductsFactory
                    .getAddedProducts({ FundingRequestId: _appInfo.FundingRequestId }, true)
                    .then(() => {
                        _hasProducts = SelectedProductsFactory.getSelectedProductsCount() > 0;

                        const HAS_DOCS_STATES = [
                            HPStateMachineConfig.Loan.Fin_Docs_Sent,
                            HPStateMachineConfig.Loan.Fin_Docs_Signed_By_All_PO,
                        ];

                        if (!_appInfo.IsMultiFundingRequest
                            && HAS_DOCS_STATES.indexOf(_stateMachineData.LoanStateId) > -1
                            && _hasRequiredDocs) {
                            _activeWalkthroughTemplate = 'app/appDetail/walkthrough/getPaid/resumeAddProducts.html';
                        } else {
                            setActiveWalkthroughTemplate();
                        }

                        resolve();
                    });
            });
        });

        return _walkthroughPromise;
    }

    function setActiveWalkthroughTemplate() {
        let activeWalkthrough = null;
        if (_stateMachineData.PipelineStageId === HPPipelineStages.GetPaid.pipelineStageId || _stateMachineData.PipelineStageId === HPPipelineStages.Closed.pipelineStageId) {
            // If PipelineStageId === 4 || 5, then look at the FundingRequestPipelineStageId instead of PipelineStageId
            if (_stateMachineData.FundingRequestPipelineStageId === HPPipelineStages.GetPaid.pipelineStageId) {
                activeWalkthrough = _.find(
                    getWalkthroughConfig()[_stateMachineData.FundingRequestPipelineStageId][_userAccess].walkthroughs,
                    walkthrough => walkthrough.condition(),
                );
            }

            if (_stateMachineData.FundingRequestPipelineStageId === HPPipelineStages.Closed.pipelineStageId) {
                activeWalkthrough = _.find(
                    getWalkthroughConfig()[_stateMachineData.FundingRequestPipelineStageId].walkthroughs,
                    walkthrough => walkthrough.condition(),
                );
            }
        } else {
            // For all other pipeline stages, the walk throughs are the same regardless of user access
            activeWalkthrough = _.find(
                getWalkthroughConfig()[_stateMachineData.PipelineStageId].walkthroughs,
                walkthrough => walkthrough.condition(),
            );
        }

        // If no appropriate template was found, use the catch all walkthrough template
        if (activeWalkthrough === undefined) {
            activeWalkthrough = {};
            activeWalkthrough.template = 'app/appDetail/walkthrough/walkthroughNotFound.html';

            // (ajt 8/8/2016) Added logging for states that do not match a predefined walkthrough
            const payload = {
                UserAccess: (_userAccess !== null && _userAccess !== undefined && _userAccess !== '') ? _userAccess : 'No User Access Info',
            };
            hpLoggerService.error('Walkthrough template was not found', 'WalkthroughFactory', 'setActiveWalkthroughTemplate', payload);

        }
        _activeWalkthroughTemplate = activeWalkthrough.template;
    }

    function getActiveWalkthroughTemplate() {
        return _activeWalkthroughTemplate;
    }

    function isUserProcessingRegeneratedCC() {
        return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Closer_Rejected_Documents && FRDocumentsFactory.getCompletionCertificateRegenerated() === true;
    }

    function isSelfServiceAddProductsEnabled() {
        // Remove DealType condition when HLP is supported.
        return PermissionsFactory.hasPermission('AddProducts.Enabled') === true && _appInfo.CanAddProductsOnline === 0 && !(_appInfo.DealType && _appInfo.DealType.DealTypeId === 5);
    }

    function getWalkthroughConfig() {
        return {
            // Begin: Application -> PipelineStageId = 2
            2: {
                walkthroughs: [
                    {
                        condition() {
                            return (_stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Application_Submitted
                                || _stateMachineData.LoanStateId === HPStateMachineConfig.Loan.AUS_Reviewed
                                || _stateMachineData.LoanStateId === HPStateMachineConfig.Loan.AUS_Denied
                                || _stateMachineData.LoanStateId === HPStateMachineConfig.Loan.AUS_Conditionally_Approved
                                || _stateMachineData.LoanStateId === HPStateMachineConfig.Loan.UW_Reviewed_Info_Needed
                                || _stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Decision_Info_Received)
                                && (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started);
                        },
                        template: 'app/appDetail/walkthrough/application/applicationResults.html',
                    },
                    {
                        condition() {
                            return (!_hasProducts
                                && _stateMachineData.LoanStateId !== HPStateMachineConfig.Loan.Application_Approved)
                        },
                        template: 'app/appDetail/walkthrough/application/applicationResults.html'
                    },
                    {
                        condition() {
                            return (_stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Application_Approved
                                || _stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Product_And_Price_Added)
                                && (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Documents_Submitted)
                                && isSelfServiceAddProductsEnabled() === false;
                        },
                        template: 'app/appDetail/walkthrough/application/callInProducts.html',
                    },
                    {
                        condition() {
                            return (_stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Application_Approved
                                || (_stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Product_And_Price_Added
                                    && !_hasProducts))
                                && (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Documents_Submitted)
                                && isSelfServiceAddProductsEnabled() === true;
                        },
                        template: 'app/appDetail/walkthrough/application/addProductsOnline.html',
                    },
                    {
                        condition() {
                            return (_stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Product_And_Price_Added
                                && _hasProducts)
                                && (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started)
                                && isSelfServiceAddProductsEnabled() === true;
                        },
                        template: 'app/appDetail/walkthrough/application/productAdded.html',
                    },
                    {
                        condition() {
                            return (_stateMachineData.LoanStateId === HPStateMachineConfig.Loan.Products_Submitted)
                                && (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started)
                                && isSelfServiceAddProductsEnabled() === true;
                        },
                        template: 'app/appDetail/walkthrough/application/productsSubmitted.html',
                    },
                ],
            },
            // End: Application -> PipelineStageId = 2
            // Begin: Start Work -> PipelineStageId = 3
            3: {
                walkthroughs: [
                    {
                        condition() {
                            return (!_hasProducts
                                && _stateMachineData.LoanStateId !== HPStateMachineConfig.Loan.Application_Approved)
                        },
                        template: 'app/appDetail/walkthrough/application/addProductsOnline.html'
                    },
                    {
                        condition() {
                            return _stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_Created
                                && _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started;
                        },
                        template: 'app/appDetail/walkthrough/startWork/CO-Review/CO-Review.html',
                    },
                    {
                        condition() {
                            return _stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_Approved_By_CO
                                && _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started;
                        },
                        template: 'app/appDetail/walkthrough/startWork/PO-Sign/PO-Email.html',
                    },
                    {
                        condition() {
                            return (_stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_Emailed_To_PO
                                || _stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_eSign_Initiated_By_PO
                                || _stateMachineData.FinDocsStateId === HPStateMachineConfig.FD.Fin_Docs_Signed_By_PO)
                                && (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started);
                        },
                        template: 'app/appDetail/walkthrough/startWork/PO-Sign/PO-Sign.html',
                    },
                ],
            },
            // End: Start Work -> PipelineStageId = 3
            // Begin: Get Paid -> PipelineStageId = 4
            4: {
                elevatedUser: {
                    walkthroughs: [
                        {
                            condition() {
                                return (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started)
                                    ||
                                    (_stateMachineData.LoanStateId === HPStateMachineConfig.FR.Project_Completed
                                        && _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started);
                            },
                            template: 'app/appDetail/walkthrough/startWork/HERO-Countersign/HERO-Countersign.html',
                        },
                        {
                            condition() {
                                return (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Completed
                                    && _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Completion_Cert_Generated)
                                    || (isUserProcessingRegeneratedCC() === true
                                        && _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Completion_Cert_Generated);
                            },
                            template: 'app/appDetail/walkthrough/getPaid/projectDetailsWalkthroughTemplate.html',
                        },
                        {
                            condition() {
                                return (_fundingRequest.FundingRequestDetail.FundingDisbursementModelTypeId === 11
                                    && ((_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Completed
                                        && _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Product_Details_Confirmed)
                                        || (isUserProcessingRegeneratedCC() === true
                                            && _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Product_Details_Confirmed)));
                            },
                            template: 'app/appDetail/walkthrough/getPaid/paymentDetailsProjectAdvanceWalkthrough.html',
                        },
                        {
                            condition() {
                                return (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Completed
                                    && _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Product_Details_Confirmed)
                                    || (isUserProcessingRegeneratedCC() === true
                                        && _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Product_Details_Confirmed);
                            },
                            template: 'app/appDetail/walkthrough/getPaid/paymentInstructionsWalkthroughTemplate.html',
                        },
                        {
                            condition() {
                                return (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Completed
                                    && _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Payment_Instructions_Confirmed)
                                    || (isUserProcessingRegeneratedCC() === true
                                        && _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Payment_Instructions_Confirmed);
                            },
                            template: 'app/appDetail/walkthrough/getPaid/getCOSignaturesWalkthrough.html',
                        },
                        {
                            condition() {
                                return (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Completed
                                    && (_stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.CO_Signed_Completion_Cert
                                        || _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.CO_Emailed_PO_Completion_Cert
                                        || _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Completion_Cert_Sent_To_Installer
                                        || _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.PO_Initiated_Completion_Cert_eSign
                                        || _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.PO_Completed_Completion_Cert_eSign))
                                    || (isUserProcessingRegeneratedCC() === true
                                        && (_stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.CO_Signed_Completion_Cert
                                            || _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.CO_Emailed_PO_Completion_Cert
                                            || _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Completion_Cert_Sent_To_Installer
                                            || _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.PO_Initiated_Completion_Cert_eSign
                                            || _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.PO_Completed_Completion_Cert_eSign));
                            },
                            template: 'app/appDetail/walkthrough/getPaid/getPOSignaturesWalkthrough.html',
                        },
                        {
                            condition() {
                                return _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Completion_Cert_Uploaded_Or_eSigned
                                    && _stateMachineData.ACAStateId !== undefined
                                    && _stateMachineData.ACAStateId !== HPStateMachineConfig.ACA.No_ACA
                                    && _stateMachineData.ACAStateId !== HPStateMachineConfig.ACA.ACA_Signed_By_All_PO
                                    && _stateMachineData.ACAStateId !== HPStateMachineConfig.ACA.COG_Countersigned_ACA;
                            },
                            template: 'app/appDetail/walkthrough/getPaid/getACASignaturesWalkthroughTemplate.html',
                        },
                        {
                            // TODO: Check optimizations turned off
                            condition() {
                                return (FRDocumentsFactory.getOutstandingDocuments().length > 0
                                    && FRDocumentsFactory.getSignaturesButtonVisibility() === false
                                    && FRClosingFactory.getPaymentInstructionsConfirmed() === true)
                                    && _stateMachineData.CompletionCertificateStateId === HPStateMachineConfig.CC.Completion_Cert_Uploaded_Or_eSigned
                                    && (_stateMachineData.ACAStateId === HPStateMachineConfig.ACA.No_ACA
                                        || _stateMachineData.ACAStateId === HPStateMachineConfig.ACA.ACA_Signed_By_All_PO
                                        || _stateMachineData.ACAStateId === HPStateMachineConfig.ACA.COG_Countersigned_ACA);
                            },
                            template: 'app/appDetail/walkthrough/getPaid/outstandingDocumentsWalkthroughTemplate.html',
                        },
                        {
                            condition() {
                                return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.All_Documents_Uploaded_Or_Signed;
                            },
                            template: 'app/appDetail/walkthrough/getPaid/projectReadyForSubmissionTemplate.html',
                        },
                        {
                            condition() {
                                return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Documents_Submitted;
                            },
                            template: 'app/appDetail/walkthrough/getPaid/projectSubmittedTemplate.html',
                        },
                        {
                            condition() {
                                return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Closer_Accepted_Case 
                                        || ((_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.HERO_Funding_Scheduled
                                        || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.HLP_Recording_Scheduled
                                        || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Completed)
                                        && _isFundingScheduled === false);
                            },
                            template: 'app/appDetail/walkthrough/getPaid/projectCloserAcceptedTemplate.html',
                        },
                        {
                            condition() {
                                return (_stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.HERO_Funding_Scheduled
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.HLP_Recording_Scheduled)
                                    && _isFundingScheduled === true;
                            },
                            template: 'app/appDetail/walkthrough/getPaid/projectApprovedWalkthroughTemplate.html',
                        },
                        {
                            condition() {
                                return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Closer_Rejected_Documents
                                    && FRDocumentsFactory.getCompletionCertificateRegenerated() === false;
                            },
                            template: 'app/appDetail/walkthrough/getPaid/rejectionsTemplate.html',
                        },
                        {
                            condition() {
                                return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.CO_Corrects_Documents;
                            },
                            template: 'app/appDetail/walkthrough/getPaid/projectReadyForReSubmissionTemplate.html',
                        },
                    ],
                },
                nonElevatedUser: {
                    walkthroughs: [
                        {
                            condition() {
                                return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Started
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Project_Completed
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.All_Documents_Uploaded_Or_Signed;
                            },
                            template: 'app/appDetail/walkthrough/getPaid/salesRepBeforeFundingWalkthroughTemplate.html',
                        },
                        {
                            condition() {
                                return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.HERO_Funding_Scheduled
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.HLP_Recording_Scheduled;
                            },
                            template: 'app/appDetail/walkthrough/getPaid/projectApprovedWalkthroughTemplate.html',
                        },
                        {
                            condition() {
                                return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Documents_Submitted
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Closer_Accepted_Case
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Closer_Rejected_Documents
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.CO_Corrects_Documents
                                    || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Final;
                            },
                            template: 'app/appDetail/walkthrough/getPaid/salesRepAfterFundingWalkthroughTemplate.html',
                        },
                    ],
                },
            },
            // End: Get Paid -> PipelineStageId = 4
            // Begin: Closed -> PipelineStageId = 5
            5: {
                walkthroughs: [
                    {
                        condition() {
                            return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.HERO_Funded
                                || _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.HLP_Completed;
                        },
                        template: 'app/appDetail/walkthrough/closed/heroFundedTemplate.html',
                    },
                    {
                        condition() {
                            return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Application_Denied;
                        },
                        template: 'app/appDetail/walkthrough/closed/applicationDeniedTemplate.html',
                    },
                    {
                        condition() {
                            return _stateMachineData.FundingRequestStateId === HPStateMachineConfig.FR.Final;
                        },
                        template: 'app/appDetail/walkthrough/closed/defaultClosedTemplate.html',
                    },
                ],
            },
            // End: Closed -> PipelineStageId = 5
        };
    }
}



// WEBPACK FOOTER //
// ./src/app/appDetail/walkthrough/walkthroughFactory.js